import { createContext, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, resolveImageUrl } from "@/lib/api";
import { applyTheme, setMetaTag, setMetaProperty, setCanonical } from "@/lib/theme";

const SiteContext = createContext(null);

export const SiteProvider = ({ children }) => {
  const [site, setSite] = useState(null);

  useEffect(() => {
    api
      .get("/site")
      .then((r) => {
        setSite(r.data);
        applyTheme(r.data.content?.theme);
        const seo = r.data.content?.seo;
        if (seo?.site_title) {
          document.title = seo.site_title;
          setMetaProperty("og:title", seo.site_title);
        }
        if (seo?.meta_description) {
          setMetaTag("description", seo.meta_description);
          setMetaProperty("og:description", seo.meta_description);
        }
        if (seo?.social_image) {
          setMetaProperty("og:image", resolveImageUrl(seo.social_image));
          setMetaTag("twitter:card", "summary_large_image");
        }
        setMetaTag("robots", r.data.indexing_allowed === false ? "noindex, nofollow" : "index, follow");
      })
      .catch(() => setSite({ content: {}, categories: [], testimonials: [] }));
  }, []);

  const categories = site?.categories || [];
  const categoryTitle = (slug) => categories.find((c) => c.slug === slug)?.title || slug;

  return (
    <SiteContext.Provider
      value={{
        site,
        loading: !site,
        content: site?.content || {},
        categories,
        testimonials: site?.testimonials || [],
        categoryTitle,
        publicUrl: site?.public_url || "",
      }}
    >
      {children}
    </SiteContext.Provider>
  );
};

export const useSite = () => useContext(SiteContext);

export const usePageSeo = (title, description, image) => {
  const { content, publicUrl } = useSite();
  const location = useLocation();
  useEffect(() => {
    const siteTitle = content?.seo?.site_title || "SSH Autosales";
    const fullTitle = title ? `${title} | ${siteTitle}` : siteTitle;
    document.title = fullTitle;
    setMetaProperty("og:title", fullTitle);
    if (publicUrl) {
      const canonicalUrl = `${publicUrl}${location.pathname}`;
      setCanonical(canonicalUrl);
      setMetaProperty("og:url", canonicalUrl);
    }
    const desc = description || content?.seo?.meta_description;
    if (desc) {
      setMetaTag("description", desc);
      setMetaProperty("og:description", desc);
    }
    if (image) {
      setMetaProperty("og:image", image);
      setMetaTag("twitter:card", "summary_large_image");
    } else if (content?.seo?.social_image) {
      setMetaProperty("og:image", resolveImageUrl(content.seo.social_image));
    }
    return () => {
      document.title = siteTitle;
      setMetaProperty("og:title", siteTitle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, image, location.pathname, content?.seo?.site_title]);
};
